/**
 * Premium SaaS theme tokens (Stripe/Notion inspired).
 *
 * Token-driven — all values reference CSS variables defined in
 * `frontend/src/index.css` so light/dark mode adapt automatically.
 *
 * NEVER use raw Tailwind palette colors (gray-500, blue-600, etc.) when
 * composing premium screens. Always use these centralized class strings.
 */

export const premiumTheme = {
  radius: {
    sm: 'rounded-md',   // 8px
    md: 'rounded-lg',   // 12px
    lg: 'rounded-xl',   // 14px
    xl: 'rounded-2xl',  // 16px
  },

  spacing: {
    xs: 'gap-1',   // 4px
    sm: 'gap-2',   // 8px
    md: 'gap-3',   // 12px
    lg: 'gap-4',   // 16px
    xl: 'gap-6',   // 24px
  },

  padding: {
    xs: 'p-2',
    sm: 'p-3',
    md: 'p-4',
    lg: 'p-5',
  },

  /**
   * Typography (respects §23.1 caps: 10\u201316px, H1 may reach 18px).
   */
  typography: {
    /** Hero H1 \u2014 up to 18px per approved amendment to \u00a723.1 */
    h1: 'text-[18px] font-semibold text-foreground leading-tight tracking-tight',
    /** Section title (14px) */
    title: 'text-[14px] font-semibold text-foreground leading-tight',
    /** Card/page subtitle (12\u201313px) */
    subtitle: 'text-[13px] text-muted-foreground leading-snug',
    /** Uppercase eyebrow label (10px) */
    label: 'text-[11px] font-medium text-muted-foreground uppercase tracking-wide',
    /** Key\u2013value data cell (12px) */
    value: 'text-[12px] font-medium text-foreground leading-snug',
    /** Table cell + input text (12px) */
    body: 'text-[12px] text-foreground leading-normal',
    /** Metadata (10px) */
    meta: 'text-[11px] text-muted-foreground',
  },

  surface: {
    /** Primary card surface with hairline border */
    card: 'bg-surface-premium border border-border-hairline rounded-xl',
    /** Secondary/muted surface (for KPI tiles, sunken panels) */
    muted: 'bg-surface-premium-muted border border-border-hairline rounded-xl',
    /** Sunken surface (deepest layer) */
    sunken: 'bg-surface-premium-sunken rounded-xl',
    /** Borderless ghost surface (for hover rows, list items) */
    ghost: 'rounded-lg',
  },

  border: {
    hairline: 'border border-border-hairline',
    strong: 'border border-border-premium',
    divider: 'border-t border-border-hairline',
  },

  motion: {
    /** Subtle transition shared across primitives */
    base: 'transition-colors duration-150 ease-out',
    hover: 'transition-all duration-150 ease-out',
  },
} as const;

export type PremiumTheme = typeof premiumTheme;
